// PartyKit real-time collaboration layer
// Each Discord channel = one PartyKit room
// NOTE: Inside Discord Activity iframe, WebSocket to external hosts is blocked by CSP.
// PartyKit will run in local-only mode inside Discord until a URL mapping is added.
import { isEmbedded } from './discord';

export interface Participant {
  userId: string;
  username: string;
  avatar: string;
  viewing: { type: string; id: string } | null;
  color: string;
}

export type ViewType = 'graph' | 'documents' | 'persons' | 'flights' | 'money';

export interface RoomState {
  view: ViewType;
  selectedEntity: { type: string; id: string } | null;
  filters: Record<string, any>;
  leader: string | null;
  participants: Record<string, Participant>;
  annotations: Annotation[];
}

export interface Annotation {
  id: string;
  userId: string;
  username: string;
  entityType: string;
  entityId: string;
  note: string;
  timestamp: number;
}

type MessageHandler = (state: RoomState) => void;

const PARTICIPANT_COLORS = [
  '#5865F2', '#57F287', '#FEE75C', '#EB459E', '#ED4245',
  '#3BA55C', '#FAA61A', '#E67E22', '#9B59B6', '#1ABC9C',
];

export class PartyClient {
  private ws: WebSocket | null = null;
  private state: RoomState;
  private handlers: Set<MessageHandler> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  private maxRetries = 2;
  private roomId: string;
  private userId: string;
  private username: string;
  private avatar: string;

  constructor(roomId: string, userId: string, username: string, avatar: string) {
    this.roomId = roomId;
    this.userId = userId;
    this.username = username;
    this.avatar = avatar;
    this.state = {
      view: 'graph',
      selectedEntity: null,
      filters: {},
      leader: null,
      participants: {},
      annotations: [],
    };
  }

  connect() {
    // Inside Discord Activity, CSP blocks WebSocket to external hosts
    // Run in local-only mode (each user sees their own state)
    if (isEmbedded) {
      this.addSelfAsParticipant();
      this.notify();
      return;
    }

    const host = import.meta.env.VITE_PARTY_HOST || 'localhost:1999';
    const protocol = host.includes('localhost') ? 'ws' : 'wss';
    const url = `${protocol}://${host}/party/${this.roomId}`;

    try {
      this.ws = new WebSocket(url);
    } catch {
      // PartyKit not available — run in local-only mode
      this.addSelfAsParticipant();
      this.notify();
      return;
    }

    this.ws.onopen = () => {
      this.retryCount = 0;
      this.send({
        type: 'join',
        userId: this.userId,
        username: this.username,
        avatar: this.avatar,
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        this.handleMessage(msg);
      } catch { /* ignore bad messages */ }
    };

    this.ws.onclose = () => {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        this.reconnectTimer = setTimeout(() => this.connect(), 3000);
      } else {
        // Give up reconnecting, stay in local-only mode
        this.addSelfAsParticipant();
        this.notify();
      }
    };

    this.ws.onerror = () => {
      // If WS fails, run in local-only mode
      this.ws?.close();
      this.addSelfAsParticipant();
      this.notify();
    };
  }

  private addSelfAsParticipant() {
    this.state.participants[this.userId] = {
      userId: this.userId,
      username: this.username,
      avatar: this.avatar,
      viewing: null,
      color: PARTICIPANT_COLORS[0],
    };
  }

  disconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  private send(msg: any) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private handleMessage(msg: any) {
    switch (msg.type) {
      case 'state':
        this.state = { ...this.state, ...msg.state };
        break;
      case 'participant_joined':
        this.state.participants[msg.participant.userId] = msg.participant;
        break;
      case 'participant_left':
        delete this.state.participants[msg.userId];
        break;
      case 'view_changed':
        this.state.view = msg.view;
        break;
      case 'entity_selected':
        this.state.selectedEntity = msg.entity;
        if (msg.userId && this.state.participants[msg.userId]) {
          this.state.participants[msg.userId].viewing = msg.entity;
        }
        break;
      case 'leader_changed':
        this.state.leader = msg.leader;
        break;
      case 'annotation_added':
        this.state.annotations.push(msg.annotation);
        break;
      case 'filters_changed':
        this.state.filters = msg.filters;
        break;
    }
    this.notify();
  }

  private notify() {
    for (const handler of this.handlers) {
      handler({ ...this.state });
    }
  }

  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    handler({ ...this.state });
    return () => this.handlers.delete(handler);
  }

  getState(): RoomState {
    return { ...this.state };
  }

  // --- Actions ---

  changeView(view: ViewType) {
    this.state.view = view;
    this.send({ type: 'view_change', view });
    this.notify();
  }

  selectEntity(entity: { type: string; id: string } | null) {
    this.state.selectedEntity = entity;
    if (this.state.participants[this.userId]) {
      this.state.participants[this.userId].viewing = entity;
    }
    this.send({ type: 'entity_select', entity, userId: this.userId });
    this.notify();
  }

  toggleLead() {
    const newLeader = this.state.leader === this.userId ? null : this.userId;
    this.state.leader = newLeader;
    this.send({ type: 'leader_toggle', leader: newLeader });
    this.notify();
  }

  addAnnotation(entityType: string, entityId: string, note: string) {
    const annotation: Annotation = {
      id: `ann-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      userId: this.userId,
      username: this.username,
      entityType,
      entityId,
      note,
      timestamp: Date.now(),
    };
    this.state.annotations.push(annotation);
    this.send({ type: 'annotation_add', annotation });
    this.notify();
  }

  updateFilters(filters: Record<string, any>) {
    this.state.filters = filters;
    this.send({ type: 'filters_update', filters });
    this.notify();
  }
}
